// controller-owned-paths.ts
//
// Bug E (dogfood vòng 4, T-0201 attempt 2-3, D08 follow-up): the verifier's
// inventory diff (attempt-baseline-inventory.ts) walks
// `git ls-files --cached --others --exclude-standard`, which — before this
// fix — saw `.claude/state/autopilot-binding.json` (a CONTROLLER-owned
// file, never any ticket's to touch) because the mission worktree's
// `.gitignore` (mission-workspace.ts's `MISSION_IGNORE_ENTRIES`) never
// listed it. A binding left over from a `contract_error` attempt (whose
// session never reached binding-rehash-and-cleanup.ts's happy-path delete —
// see session-runner-stream-consumer.ts's `no_result_json_found` branch)
// then got swept into `git add .` by a LATER `run` invocation's
// `ensureBootstrapCommit` (bootstrapManifestPaths: ["."]), making it
// TRACKED — after which every later attempt's own fresh binding write read
// as a scope-violating "modified"/"deleted" diff against
// `allowed_paths`, even though no ticket's `allowed_paths` was ever meant
// to cover it.
//
// Single source of truth so the mission's `.gitignore` (mission-workspace.ts)
// and the verifier's scope check (result-verifier-checks.ts) can never
// drift apart on "which paths are the controller's, not a ticket's".
import { matchesGlob, pathAllowed } from "./result-verifier-checks.js";

/** The approved-recipe list the verifier judges every attempt against — named so the verifier can single it out (result-verifier.ts step 5b). */
export const VERIFICATION_PROFILE_PATH = ".avatar/verification-profile.json";

/**
 * Phase 06f R1 (H3): the DAG index the controller renders from validated
 * tickets (controller-plan-index-markdown.ts re-exports it). Declared HERE,
 * not imported from that module, so this file never imports back into the
 * PLAN renderer (one-way edge; no cycle/TDZ while the array below initialises).
 */
export const PLAN_INDEX_PATH = ".avatar/PLAN.md";

/**
 * Glob patterns (same mini-glob syntax as `allowed_paths`/`report_glob` —
 * see `matchesGlob` in result-verifier-checks.ts) for paths the CONTROLLER
 * itself owns inside a mission worktree — never a ticket's to create,
 * modify, or delete: the binding directory (`autopilot-binding-file-writer.ts`
 * is the only writer under `.claude/state/`), the verification profile, and
 * the PLAN index; kept as a list so a future controller-owned artifact can be
 * added here without touching every call site that consults it.
 */
export const CONTROLLER_OWNED_PATH_GLOBS = [
  ".claude/state/**",
  // P22: `.avatar/verification-profile.json` is the list of recipes the
  // verifier will judge this worker's attempt against — and it lived inside
  // the worker's own worktree, in neither this list nor
  // MISSION_IGNORE_ENTRIES. Measured before the fix:
  // `pathAllowed(".avatar/verification-profile.json", ["**"])` => true, so a
  // ticket scoped `**` could rewrite it and `loadApprovedRecipes`
  // (supervisor-attempt-cycle.ts, re-read LIVE every tick) would hand
  // `checkRecipeIsApproved` the worker's own recipes. The governed party
  // editing the rule it is judged by — the Bug K/Bug L shape.
  // g2-baseline-exception-mark.ts named this in prose at P17 ("THE CATCH")
  // and closed only the half feeding G2's decision; the recipe list stayed
  // live. Excluding the path here means a worker edit to it is no longer
  // coverable by ANY ticket's allowed_paths, so the verifier's scope check
  // reports it as out of scope instead of waving it through.
  VERIFICATION_PROFILE_PATH,
  // 06f R1: no controller step writes it mid-attempt (done-path after verify,
  // redirect at a tick boundary), so a worker edit is refused at 5b too.
  PLAN_INDEX_PATH,
  // Security audit H-1: ticket files are written only by the controller
  // (proposal application, redirect, recovery — all after verification or at
  // a tick boundary) and read back from disk; a worker file here shadows or
  // adds a ticket. No ticket's scope may cover it.
  ".avatar/tickets/**",
] as const;

/** True if `path` (repo-relative, forward-slash) is controller-owned per `CONTROLLER_OWNED_PATH_GLOBS`. */
export function isControllerOwnedPath(path: string): boolean {
  return CONTROLLER_OWNED_PATH_GLOBS.some((pattern) => matchesGlob(pattern, path));
}

/**
 * True when `pattern` (an `allowed_paths` entry) would MATCH a
 * controller-owned path — the mirror of `isControllerOwnedPath`, which asks
 * whether a concrete path is owned. P22 needs this direction because a
 * proposal declares patterns, not paths: `".avatar/**"` is not itself a
 * controller-owned path, but it reaches one, and a ticket carrying it would
 * put `verification-profile.json` inside its own declared scope.
 *
 * Both directions live here so the proposal boundary (proposal-applier.ts)
 * and the verifier's scope check can never drift apart on what counts as
 * the controller's.
 */
export function coversAControllerOwnedPath(pattern: string): boolean {
  // 06f R1: judged with the verifier's OWN matcher (`pathAllowed`: glob OR
  // directory-prefix form), not `matchesGlob` alone — otherwise `.avatar` /
  // `.avatar/` passed this guard while the verifier read them as covering
  // every file under `.avatar/`.
  return CONTROLLER_OWNED_PATH_GLOBS.some((owned) => {
    // An owned entry that is itself a glob (".claude/state/**") is covered
    // when the pattern reaches a representative path underneath it.
    const probe = owned.endsWith("/**") ? `${owned.slice(0, -3)}/any-file` : owned;
    return pathAllowed(probe, [pattern]);
  });
}

/**
 * Splits an inventory diff's touched paths into the ones the scope check
 * should judge (`kept`) and the controller-owned ones it must skip
 * (`excluded`).
 *
 * P22 (code-reviewer follow-up): the exclusion exists so a leftover binding
 * file cannot read as a scope violation (Bug E), and it is deliberately
 * silent about that. But P22 added `.avatar/verification-profile.json` — the
 * recipe list `checkRecipeIsApproved` honours — to the owned set, so the same
 * silence now covers the highest-value file in the worktree. An edit to it
 * should be impossible (checkProposedScope refuses the scope at the proposal
 * boundary), which is exactly why one actually happening is worth saying out
 * loud: it means that boundary was bypassed by a route nobody anticipated.
 * Returning `excluded` lets the verifier report it; silently dropping it
 * would make the edit invisible rather than refused, and "no record" is
 * indistinguishable from "never happened".
 */
export function partitionControllerOwnedPaths(paths: string[]): {
  kept: string[];
  excluded: string[];
} {
  const kept: string[] = [];
  const excluded: string[] = [];
  for (const p of paths) {
    if (isControllerOwnedPath(p)) excluded.push(p);
    else kept.push(p);
  }
  return { kept, excluded };
}
